import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Linkedin } from 'lucide-react';

// Curated editorial recommendations — used across Home, Pathway, Academy.
// Excerpts only. Designed to read as a single rotating pull quote, not a card deck.
const RECOMMENDATIONS = [
  {
    id: 'alexandra-mirutoi',
    name: 'Alexandra Mirutoi',
    role: 'VVIP Flight Attendant — Saudi Royal Aviation',
    photo: '/recommendations/alexandra.png',
    excerpt:
      'The training goes far beyond service and focuses on the real operational side of business aviation.',
  },
  {
    id: 'matteo-chivilo',
    name: 'Matteo Chivilo',
    role: 'Cabin Senior — Etihad Airways',
    photo: '/recommendations/matteo.png',
    excerpt:
      'What stood out immediately was his understanding of what private aviation operators are actually looking for beyond a standard airline profile.',
  },
  {
    id: 'thomas-defrance',
    name: 'Thomas Defrance',
    role: 'Cabin Crew — Transavia',
    photo: '/recommendations/thomas.png',
    excerpt:
      'The training gives you a real grasp of what the industry actually demands: autonomy, flight organisation, catering, mindset, positioning, recruitment, and the real expectations of operators.',
  },
  {
    id: 'marie-teysseyre',
    name: 'Marie Teysseyre',
    role: 'Premium Flight Attendant — Etihad Airways',
    photo: '/recommendations/marie.png',
    excerpt:
      'The operational reality behind the training is heavy, and he explains the real expectations of private aviation.',
  },
];

const LINKEDIN_URL = 'https://www.linkedin.com/in/nicolaspds/';
const ROTATION_MS = 8000;

const Recommendations = ({
  variant = 'light',
  eyebrow = 'Industry Perspective',
  title = 'Operational Reality Through Their Eyes',
  testId = 'recommendations-section',
  inline = false,        // when true: no <section>, no bg, no outer padding — render inside parent
  centered = false,      // center align header + stage
  compact = false,       // reduce vertical paddings
}) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const isDark = variant === 'dark';
  const titleColor = isDark ? 'text-[#FDFBF7]' : 'text-[#1A1A1A]';
  const subColor = isDark ? 'text-[#9A9A9A]' : 'text-[#5A5A5A]';
  const dotInactive = isDark ? 'bg-[#FDFBF7]/25' : 'bg-[#1A1A1A]/15';

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => {
      setIdx((i) => (i + 1) % RECOMMENDATIONS.length);
    }, ROTATION_MS);
    return () => clearTimeout(t);
  }, [idx, paused]);

  const goTo = useCallback((i) => setIdx(i % RECOMMENDATIONS.length), []);
  const current = RECOMMENDATIONS[idx];

  const alignWrap = centered ? 'mx-auto text-center' : '';
  const headerMax = centered ? 'max-w-2xl mx-auto text-center' : 'max-w-2xl';
  const stageMax = centered ? 'max-w-3xl mx-auto' : 'max-w-4xl';
  const stageRowJustify = centered ? 'justify-center' : '';
  const quoteAlign = centered ? 'text-center' : '';
  const attributionLayout = centered
    ? 'flex flex-col items-center gap-3'
    : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2';

  // ---------- Content (used both inline and standalone) ----------
  const content = (
    <div
      className={`${alignWrap}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={`${headerMax} ${compact ? 'mb-8 md:mb-10' : 'mb-10 md:mb-12'}`}>
        <span
          className="text-xs uppercase tracking-[0.24em] font-medium text-[#C5A880] inline-block mb-3"
          data-testid="recommendations-eyebrow"
        >
          {eyebrow}
        </span>
        <h2
          className={`font-heading ${
            compact ? 'text-2xl sm:text-[28px] lg:text-[32px]' : 'text-2xl sm:text-3xl lg:text-[36px]'
          } tracking-tight leading-[1.15] ${titleColor}`}
          data-testid="recommendations-title"
        >
          {title}
        </h2>
      </div>

      <div
        className={`relative ${stageMax}`}
        style={{ minHeight: 180 }}
        data-testid="recommendations-stage"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
            data-testid={`recommendation-${current.id}`}
          >
            {centered ? (
              // ---- Editorial centered layout: quote on top, photo + name below ----
              <div className="flex flex-col items-center">
                <blockquote className="max-w-2xl">
                  <p
                    className={`font-heading text-base md:text-lg lg:text-[22px] leading-[1.5] tracking-tight ${titleColor} text-center`}
                    style={{ fontWeight: 400 }}
                  >
                    <span className="text-[#C5A880] mr-1.5" aria-hidden="true">“</span>
                    {current.excerpt}
                    <span className="text-[#C5A880] ml-1" aria-hidden="true">”</span>
                  </p>
                </blockquote>

                <div className="mt-7 flex items-center gap-4">
                  <img
                    src={current.photo}
                    alt={`Gate 33 private aviation training and consulting — ${current.name}, ${current.role}`}
                    title={`Gate 33 private aviation training and consulting — ${current.name}`}
                    loading="lazy"
                    className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0"
                    style={{ border: '1px solid rgba(197, 168, 128, 0.4)' }}
                  />
                  <div className="text-left">
                    <div className={`text-sm font-medium tracking-tight ${titleColor}`}>
                      {current.name}
                    </div>
                    <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${subColor}`}>
                      {current.role}
                    </div>
                  </div>
                </div>

                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`recommendation-link-${current.id}`}
                  className={`mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] ${subColor} hover:text-[#C5A880] transition-colors duration-300 group whitespace-nowrap`}
                >
                  <span className="border-b border-current pb-0.5 group-hover:border-[#C5A880]">
                    Read on LinkedIn
                  </span>
                  <Linkedin size={12} strokeWidth={1.5} aria-hidden="true" />
                </a>
              </div>
            ) : (
              // ---- Left-aligned layout (legacy): photo next to quote row ----
              <div className={`flex items-start gap-5 md:gap-7 ${stageRowJustify}`}>
                <img
                  src={current.photo}
                  alt={`Gate 33 private aviation training and consulting — ${current.name}, ${current.role}`}
                  title={`Gate 33 private aviation training and consulting — ${current.name}`}
                  loading="lazy"
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover flex-shrink-0 mt-1"
                  style={{ border: '1px solid rgba(197, 168, 128, 0.4)' }}
                />

                <div className="flex-1 min-w-0">
                  <blockquote>
                    <p
                      className={`font-heading text-base md:text-lg lg:text-[22px] leading-[1.5] tracking-tight ${titleColor}`}
                      style={{ fontWeight: 400 }}
                    >
                      <span className="text-[#C5A880] mr-1.5" aria-hidden="true">“</span>
                      {current.excerpt}
                      <span className="text-[#C5A880] ml-1" aria-hidden="true">”</span>
                    </p>
                  </blockquote>

                  <div className={`mt-5 ${attributionLayout}`}>
                    <div>
                      <div className={`text-sm font-medium tracking-tight ${titleColor}`}>
                        {current.name}
                      </div>
                      <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${subColor}`}>
                        {current.role}
                      </div>
                    </div>
                    <a
                      href={LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`recommendation-link-${current.id}`}
                      className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] ${subColor} hover:text-[#C5A880] transition-colors duration-300 group whitespace-nowrap`}
                    >
                      <span className="border-b border-current pb-0.5 group-hover:border-[#C5A880]">
                        Read on LinkedIn
                      </span>
                      <Linkedin size={12} strokeWidth={1.5} aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div
          className={`flex items-center gap-2 mt-8 ${centered ? 'justify-center' : ''}`}
          role="tablist"
          data-testid="recommendations-dots"
        >
          {RECOMMENDATIONS.map((r, i) => (
            <button
              key={r.id}
              type="button"
              aria-label={`Show recommendation from ${r.name}`}
              aria-selected={i === idx}
              role="tab"
              onClick={() => goTo(i)}
              data-testid={`recommendation-dot-${i}`}
              className="group p-1.5 -m-1.5"
            >
              <span
                className={`block h-[2px] transition-all duration-500 ${
                  i === idx ? 'w-8 bg-[#C5A880]' : `w-4 ${dotInactive}`
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ---------- Render ----------
  if (inline) {
    // No section wrapper, no background — render inside parent container
    return (
      <div className={compact ? 'pt-10 md:pt-14' : 'pt-12 md:pt-16'} data-testid={testId}>
        {content}
      </div>
    );
  }

  const bg = isDark ? 'bg-[#1A1A1A]' : 'bg-[#FDFBF7]';
  const pad = compact ? 'py-10 md:py-14' : 'py-16 md:py-20';
  return (
    <section data-testid={testId} className={`${bg} ${pad}`}>
      <div className="container-custom">{content}</div>
    </section>
  );
};

export default Recommendations;
